import { ref, uploadBytesResumable, getDownloadURL, deleteObject, uploadString } from "firebase/storage";
import { storage, auth } from "./firebase";
import { v4 as uuidv4 } from "uuid";

// Upload file and return download URL
export const uploadFile = async (file, userId, invitationId = null, onProgress = null) => {
  console.log("🚀 UPLOAD FILE FUNCTION CALLED WITH:", {
    fileName: file.name,
    fileSize: `${(file.size / 1024).toFixed(2)} KB`,
    fileType: file.type,
    userId,
    invitationId
  });
  
  if (!userId) {
    console.error("❌ Upload error: No user ID provided");
    throw new Error("Authentication required for uploads");
  }
  
  // Check if Firebase auth is working
  console.log("🔐 Auth state:", {
    currentUser: auth.currentUser ? "exists" : "null",
    currentUserUid: auth.currentUser?.uid,
    providedUserId: userId
  });
  
  // Generate file path - include invitationId if available for better organization
  const fileId = uuidv4();
  const fileExtension = file.name.split('.').pop().toLowerCase();
  const sanitizedFileName = file.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  
  // Determine media type folder based on file MIME type
  let mediaType = 'files';
  if (file.type.startsWith('image/')) {
    mediaType = 'images';
  } else if (file.type.startsWith('video/')) {
    mediaType = 'videos';
  }
  
  // Create a more organized path structure
  let path;
  if (invitationId) {
    path = `invitations/${invitationId}/${mediaType}/${fileId}-${sanitizedFileName}`;
  } else {
    path = `users/${userId}/${mediaType}/${fileId}-${sanitizedFileName}`;
  }
  
  console.log("📂 Using storage path:", path);
  
  try {
    // Check if storage is properly initialized
    if (!storage) {
      console.error("❌ Firebase Storage is not initialized");
      throw new Error("Storage service is not available");
    }
    
    console.log("📦 Storage object state:", storage ? "exists" : "null");
    
    // Create storage reference
    const storageRef = ref(storage, path);
    console.log("📑 Storage reference created:", storageRef);
    
    // Create upload task - directly upload the file, no base64 conversion
    console.log("⬆️ Creating upload task...");
    const uploadTask = uploadBytesResumable(storageRef, file);
    console.log("✅ Upload task created");
    
    // Return a promise that resolves when complete
    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        // Progress callback
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`🔄 Upload progress: ${Math.round(progress)}%`);
          if (onProgress) onProgress(progress);
        },
        // Error callback
        (error) => {
          console.error('❌ Upload error:', error);
          reject(error);
        },
        // Success callback
        async () => {
          try {
            // Get download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log("✅ Upload complete:", downloadURL);
            resolve({
              url: downloadURL,
              path: path,
              id: fileId,
              fileName: file.name
            });
          } catch (error) {
            console.error("❌ Error getting download URL:", error);
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error("❌ Error creating upload task:", error);
    throw error;
  }
};

// Delete file from storage
export const deleteFile = async (path) => {
  if (!path) {
    console.error("❌ Cannot delete file: No path provided");
    return;
  }
  
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    console.log("✅ File deleted successfully:", path);
  } catch (error) {
    console.error("❌ Error deleting file:", error);
    throw error;
  }
};

// Test connection to Firebase Storage
export const testStorageConnection = async () => {
  try {
    console.log("🧪 Testing Firebase Storage connection...");
    const testRef = ref(storage, "test/connection-test.txt");
    const testUploadTask = uploadBytesResumable(
      testRef, 
      new Blob(["Test connection " + new Date().toISOString()], { type: "text/plain" })
    );
    
    return new Promise((resolve, reject) => {
      testUploadTask.on(
        'state_changed',
        snapshot => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Test upload progress: ${progress.toFixed(2)}%`);
        },
        error => {
          console.error("❌ Test upload failed:", error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(testUploadTask.snapshot.ref);
            console.log("✅ Test upload successful. File available at:", downloadURL);
            resolve(downloadURL);
          } catch (error) {
            console.error("❌ Failed to get download URL:", error);
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error("❌ Test failed:", error);
    throw error;
  }
};

// Add a diagnostic function to test Firebase Storage configuration
export const diagnoseStorageConfig = () => {
  console.group("🔍 FIREBASE STORAGE DIAGNOSTICS");
  try {
    // Check if Firebase is properly loaded
    console.log("Storage object exists:", !!storage);
    
    // Check storage configuration
    if (storage) {
      const app = storage.app;
      console.log("Firebase app associated with storage:", !!app);
      
      if (app) {
        console.log("Storage bucket:", app.options.storageBucket || "Not set");
        console.log("Project ID:", app.options.projectId || "Not set");
        console.log("API Key:", app.options.apiKey ? "Set (hidden)" : "Not set");
      }
      
      // Check auth state
      console.log("Auth initialized:", !!auth);
      console.log("Current user:", auth.currentUser ? `Signed in as ${auth.currentUser.uid}` : "Not signed in");
    } else {
      console.error("Storage not initialized - check Firebase config");
    }
    
    // Check CORS configuration
    console.log("CORS: To verify CORS is properly set up, try a test upload");
    console.log("If uploads work but you can't access the files, run this command in your terminal:");
    console.log("gsutil cors set cors.json gs://ejoy-55ca1.firebasestorage.app");
    
    console.groupEnd();
    return {
      isInitialized: !!storage,
      bucket: storage?.app?.options?.storageBucket || null,
      isAuthenticated: !!auth.currentUser
    };
  } catch (error) {
    console.error("Error in diagnostics:", error);
    console.groupEnd();
    return { error: error.message };
  }
};

// Add this to your storage.js temporarily:
console.log("⚙️ FIREBASE CONFIG:", {
  apiKey: "REDACTED", // Don't log the actual key
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "not set",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "not set",
  isStorageInitialized: !!storage
});

// Upload template thumbnail and return download URL
export const uploadTemplateThumbnail = async (thumbnailDataUrl, userId, templateId) => {
  console.log("🔄 [TemplateThumbnail] Upload function called with:", {
    thumbnailProvided: !!thumbnailDataUrl,
    thumbnailType: typeof thumbnailDataUrl,
    thumbnailLength: thumbnailDataUrl?.length,
    thumbnailIsValid: thumbnailDataUrl?.startsWith('data:image/'),
    userId,
    templateId
  });
  
  if (!userId) {
    console.error("❌ [TemplateThumbnail] Upload error: No user ID provided");
    throw new Error("Authentication required for uploads");
  }
  
  if (!templateId) {
    console.error("❌ [TemplateThumbnail] Upload error: No template ID provided");
    throw new Error("Template ID required for thumbnail upload");
  }
  
  if (!thumbnailDataUrl || !thumbnailDataUrl.startsWith('data:image/')) {
    console.error("❌ [TemplateThumbnail] Invalid thumbnail data provided:", 
      thumbnailDataUrl ? thumbnailDataUrl.substring(0, 30) + '...' : 'null or undefined');
    throw new Error("Invalid thumbnail data format");
  }
  
  // Check if Firebase auth is working
  console.log("🔐 [TemplateThumbnail] Auth state:", {
    currentUser: auth.currentUser ? "exists" : "null",
    currentUserUid: auth.currentUser?.uid,
    providedUserId: userId,
    idsMatch: auth.currentUser?.uid === userId
  });
  
  // Create path using the same pattern as image/video uploads
  const path = `users/${userId}/templates/${templateId}/thumbnail.png`;
  
  // Log the detailed path structure for debugging
  console.log("📁 [TemplateThumbnail] Path structure details:", {
    fullPath: path,
    userSegment: `users/${userId}`,
    templatesFolder: `users/${userId}/templates`,
    templateFolder: `users/${userId}/templates/${templateId}`,
    fileName: "thumbnail.png"
  });
  
  try {
    // Check if storage is properly initialized
    if (!storage) {
      console.error("❌ [TemplateThumbnail] Firebase Storage is not initialized");
      throw new Error("Storage service is not available");
    }
    
    // Create the storage reference
    const storageRef = ref(storage, path);
    console.log("📑 [TemplateThumbnail] Storage reference created:", {
      fullPath: storageRef.fullPath,
      bucket: storageRef.bucket,
      name: storageRef.name,
      parent: storageRef.parent ? storageRef.parent.fullPath : "none" // Check parent path
    });
    
    // Upload the data URL with retry logic
    let uploadSuccess = false;
    let uploadAttempts = 0;
    const maxAttempts = 3;
    let uploadError = null;
    
    while (!uploadSuccess && uploadAttempts < maxAttempts) {
      uploadAttempts++;
      console.log(`⬆️ [TemplateThumbnail] Starting upload attempt ${uploadAttempts}/${maxAttempts}...`);
      console.time(`thumbnailUpload-attempt-${uploadAttempts}`);
      
      try {
        await uploadString(storageRef, thumbnailDataUrl, 'data_url');
        console.timeEnd(`thumbnailUpload-attempt-${uploadAttempts}`);
        console.log(`✅ [TemplateThumbnail] Upload succeeded on attempt ${uploadAttempts}`);
        uploadSuccess = true;
      } catch (error) {
        console.timeEnd(`thumbnailUpload-attempt-${uploadAttempts}`);
        console.error(`❌ [TemplateThumbnail] Upload failed on attempt ${uploadAttempts}:`, error);
        uploadError = error;
        
        // If we haven't reached max attempts, wait before retrying
        if (uploadAttempts < maxAttempts) {
          const waitTime = 1000 * uploadAttempts; // Increase wait time with each attempt
          console.log(`⏱️ [TemplateThumbnail] Waiting ${waitTime}ms before retrying...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
    }
    
    // If we've exhausted all attempts and still failed, throw the last error
    if (!uploadSuccess) {
      console.error(`❌ [TemplateThumbnail] All ${maxAttempts} upload attempts failed`);
      throw uploadError;
    }
    
    // Get download URL
    console.log("🔍 [TemplateThumbnail] Getting download URL...");
    console.time('getDownloadURL');
    let downloadURL;
    
    try {
      downloadURL = await getDownloadURL(storageRef);
      console.timeEnd('getDownloadURL');
      console.log("✅ [TemplateThumbnail] Download URL obtained:", downloadURL);
    } catch (urlError) {
      console.timeEnd('getDownloadURL');
      console.error("❌ [TemplateThumbnail] Error getting download URL:", urlError);
      throw urlError;
    }
    
    // Verify the URL is valid
    if (!downloadURL || typeof downloadURL !== 'string' || !downloadURL.startsWith('http')) {
      console.error("❌ [TemplateThumbnail] Invalid download URL obtained:", downloadURL);
      throw new Error("Failed to get valid download URL for thumbnail");
    }
    
    // Try to verify the templates folder was created correctly
    try {
      // Check if the user folder exists or was created
      const userFolderRef = ref(storage, `users/${userId}`);
      console.log("🔍 [TemplateThumbnail] User folder reference:", userFolderRef.fullPath);
      
      // Check if the templates folder was created
      const templatesFolderRef = ref(storage, `users/${userId}/templates`);
      console.log("🔍 [TemplateThumbnail] Templates folder reference:", templatesFolderRef.fullPath);
      
      // Check if the specific template folder was created
      const templateFolderRef = ref(storage, `users/${userId}/templates/${templateId}`);
      console.log("🔍 [TemplateThumbnail] Template folder reference:", templateFolderRef.fullPath);
    } catch (verifyError) {
      console.warn("⚠️ [TemplateThumbnail] Error verifying folder structure:", verifyError);
      // Continue anyway as the thumbnail upload was successful
    }
    
    // Log full result for debugging
    const result = {
      url: downloadURL,
      path: path
    };
    
    console.log("🎉 [TemplateThumbnail] Upload completed successfully:", result);
    return result;
  } catch (error) {
    console.error("❌ [TemplateThumbnail] Error uploading template thumbnail:", error);
    console.error("❌ [TemplateThumbnail] Error details:", {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    throw error;
  }
}; 